import { Button } from '@components/Button';
import { Label } from '@components/styled/Label.styled';
import { Subtitle } from '@components/styled/Subtitle.styled';
import { Title } from '@components/styled/Title.styled';
import { datadogLogs } from '@datadog/browser-logs';
import { updateUnitFeatures } from '@lib/api';
import { PropertyType } from '@models/PropertyType';
import { RentProjectionRequest } from '@models/RentProjectionRequest';
import { RentProjectionResponse } from '@models/RentProjectionResponse';
import { VacasaLogo } from '@src/assets';
import { useAuthorization } from '@vacasa/react-components-lib';
import { AxiosResponse } from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { UnitFeatures } from '../../codecs/revenueEstimate';
import * as styled from './App.styled';
import { AddressGroup } from './components/AddressGroup';
import { AddressVerifySubmitGroup } from './components/AddressVerifySubmitGroup';
import { AmenitiesGroup } from './components/AmenitiesGroup';
import { Chip } from './components/Chip';
import { ErrorGroup } from './components/ErrorGroup';
import { HomeTypeGroup } from './components/HomeTypeGroup';
import { MaxOccupancyGroup } from './components/MaxOccupancyGroup';
import { MinStayGroup } from './components/MinStayGroup';
import { OwnerHoldsGroup } from './components/OwnerHoldsGroup';
import { RecalculateGroup } from './components/RecalculateGroup';
import { RoomsGroup } from './components/RoomsGroup';
import { SquareFootageGroup } from './components/SquareFootageGroup';
import { Header } from './components/styled/Header.styled';
import { SubmitGroup } from './components/SubmitGroup';
import { SurroundingsGroup } from './components/SurroundingsGroup';
import { useRealEstateMutation } from './hooks/useRealEstate';
import { useUnitFeaturesMutation, useUnitFeaturesQuery } from './hooks/useUnitFeatures';
import './index.scss';
import { identifyUser, trackAnalytics } from './lib/analytics';
import { initAppmode, APPMODE } from './lib/appmode';
import { normalizeAddress } from './lib/address';
import { MinRateGroup } from './components/MinRateGroup';
import { SubSubTitle } from '@components/styled/SubSubTitle.styled';
import { VacasaComps } from './components/CompUnits';

export type EstimatorForm = RentProjectionRequest;

/**
 * The form data is all over the place and messy, leads to errors. This var is a quick fix for NT-1169.
 * Ideally we have a better systematic way of defining the form fields and their types. Right now these
 * fields have hard coded references everywhere in the app which makes hard to make sure everything is
 * working as expected.  Maybe bake into each component a way to get field names, default values, set values, etc.
 * instead of doing it manually in the main app here.
 */
const formFields: {static: (keyof EstimatorForm)[], reset: (keyof EstimatorForm)[]} = {
    static: ['address', 'propertyType', 'bathrooms', 'bedrooms'],
    reset: [
    'isWaterfront',
    'isSkiInSkiOut',
    'hasPrivatePool',
    'has_private_hot_tub',
    'has_gas_grill',
    'has_washerdryer',
    'has_gas_fireplace',
    'hasOven',
    'maxOccupancy',
    'squareFootage',
    'num_owner_holds',
    'min_rate',
    'minMinStay',
]};

export interface AdminAppProps {
    unitId?: string;
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    marketName?: string;
}

/**
 * Revenue Estimate App.
 * - https://www.figma.com/file/qrBq2419edxAOzKH4A4YWH/Revenue-Estimate-App?node-id=612%3A915
 * - https://vacasait.atlassian.net/browse/RC-869
 */
export const AdminApp: React.FC<AdminAppProps> = ({
    unitId,
    street,
    city,
    state,
    zip,
    country,
    marketName,
}) => {
    const scrollUpRef = useRef<HTMLDivElement>(null);
    const [errorTitle, setErrorTitle] = useState<string>();
    const [errors, setErrors] = useState<string>();
    const [disabled, setDisabled] = useState(true);
    const [showFirstResetBtn, setShowFirstResetBtn] = useState(false);
    const [disabledAddressVerifiedBtn, setDisabledAddressVerifiedBtn] = useState(true);
    const [addressVerified, setAddressVerified] = useState(false);
    const [isRecalculated, setIsRecalculated] = useState(false);
    const auth = useAuthorization();
    const params = new URLSearchParams(window.location.search);
    const address = [street, city, state, zip, country].join(', ');
    const unitFeaturesMutate = useUnitFeaturesMutation();
    const [estimateData, setEstimateData] = useState<
        RentProjectionResponse | UnitFeatures | undefined
    >();

    const checkAndLogUnitDetails = (eventName: string, objectToLog?: Record<string, unknown>) => {
        if (
            !street ||
            !city ||
            !state ||
            !zip ||
            !country ||
            window.location.search.includes('{Unit__c.')
        ) {
            datadogLogs.logger.warn(eventName, { params, objectToLog });
        }
    };

    const methods = useForm<EstimatorForm>({
        reValidateMode: 'onSubmit',
        defaultValues: {
            address,
            propertyType: '' as PropertyType,
            bathrooms: 0,
            bedrooms: 0,
            isWaterfront: false,
            isSkiInSkiOut: false,
            hasPrivatePool: false,
            has_private_hot_tub: false,
            has_gas_grill: false,
            has_washerdryer: false,
            has_gas_fireplace: false,
            hasOven: false,
            squareFootage: undefined,
            num_owner_holds: 0,
            min_rate: 0,
            minMinStay: 0,
            maxOccupancy: undefined,
        },
    });

    const { getValues, watch, reset: resetForm, resetField, setValue } = methods;
    const watchRequiredFields = watch([
        'bathrooms',
        'propertyType',
        'maxOccupancy',
        'squareFootage',
    ]);

    useEffect(() => {
        const enabledDisabledAddressVerifiedBtn =
            getValues('bathrooms') > 0 && !!getValues('propertyType');

        setDisabledAddressVerifiedBtn(!enabledDisabledAddressVerifiedBtn);

        setShowFirstResetBtn(getValues('bathrooms') > 0 || !!getValues('propertyType'));

        const enabled =
            getValues('bathrooms') > 0 &&
            !!getValues('maxOccupancy') &&
            !!getValues('squareFootage') &&
            !!getValues('propertyType');
        setDisabled(!enabled);
    }, [watchRequiredFields]);

    useEffect(() => {
        identifyUser(auth?.idToken);
        trackAnalytics('Revenue Estimate App Loaded');
        checkAndLogUnitDetails('Revenue estimate app loaded without full unit details');
    }, []);

    useEffect(() => {
        refetch();
    }, []);

    const getFeaturesSuccess = (data?: UnitFeatures) => {
        if (!data) return;
        const { ...formValues } = data;

        Object.entries(formValues).forEach(([key, value]: any) => {
            if (!formFields.static.includes(key) && !formFields.reset.includes(key)) return; //if not form field, skip
            setValue(key, value);
        });

        setAddressVerified(true);
        setIsRecalculated(true);
        setEstimateData(data);
    };

    const { isFetching, refetch } = useUnitFeaturesQuery(unitId, getFeaturesSuccess);

    const {
        mutateAsync,
        isLoading,
        reset: resetMutation,
    } = useRealEstateMutation({
        unitId,
        marketName,
        getValues,
    });

    const onVerifyAddressHandler = async () => {
        const address = getValues('address');
        const bathrooms = getValues('bathrooms');
        const bedrooms = getValues('bedrooms');

        if (unitId) {
            await unitFeaturesMutate.mutateAsync({
                unitId,
                address,
                bathrooms,
                bedrooms,
            });
        }
        setAddressVerified(true);
        trackAnalytics('Revenue Estimate Address Verified', getBaseTrackingData());
    };

    const onResetAmenities: React.ReactEventHandler<Element> = (e) => {
        e.preventDefault();
        formFields.reset.forEach((f) => resetField(f));
        trackAnalytics('Revenue Estimate Reset', {
            ...getBaseTrackingData(),
            section: 'Amenities',
        });
    };

    const onGetRentProjectionSuccess = (recalculating?: boolean) => async (res: AxiosResponse) => {
        if (200 > res.status || 300 <= res.status) {
            let body: string;
            try {
                body = JSON.stringify(res.data);
            } catch (err) {
                body = 'Unknown Error: ' + (err as any).message;
            }

            setErrorTitle(`${res.status} ${res.statusText}`);
            setErrors(body);
            return;
        }

        const projection: RentProjectionResponse = res.data.data.attributes;
        setAddressVerified(true);
        setIsRecalculated(true);
        trackAnalytics(`Revenue Estimate ${recalculating ? 'Recalculated' : 'Submitted'}`, {
            ...getValues(),
            ...getBaseTrackingData(),
            low_estimate: projection?.prediction_band_low,
            estimated_value: projection?.annualPrediction,
            high_estimate: projection?.prediction_band_high,
            execution_id: projection?.execution_id,
        });

        if (unitId) {
            await updateUnitFeatures(unitId, {
                ...getValues(),
                annualPrediction: projection.annualPrediction,
                annual_prediction_source: projection.annual_prediction_source,
                monthlyPredictions: projection.monthlyPredictions,
                calendarYearMonthlyPredictions: projection.calendarYearMonthlyPredictions,
                compVacasaUnits: projection.compVacasaUnits,
                unitid_similar: projection.unitid_similar,
                prediction_band_low: projection.prediction_band_low,
                prediction_band_high: projection.prediction_band_high,
            });
        }

        setEstimateData(projection);
    };

    const onError = (err: any) => {
        let title, body: string;
        try {
            title = err.message;
            body = JSON.stringify(err);
        } catch (err2) {
            title = 'Unknown Error';
            body = (err2 as Error).message;
        }
        setErrorTitle(title);
        setErrors(body);
    };

    const onResetAddress: React.ReactEventHandler<Element> = (e) => {
        e.preventDefault();
        setAddressVerified(false);
        setIsRecalculated(false);
        setErrorTitle(undefined);
        setErrors(undefined);
        resetForm();
        resetMutation();
        setEstimateData(undefined);

        trackAnalytics('Revenue Estimate Reset', {
            ...getBaseTrackingData(),
            section: 'Address',
        });

        scrollUpRef.current?.scrollIntoView({ behavior: 'smooth' });
    };
    const disabledOrBadAddress = disabledAddressVerifiedBtn || !addressVerified;

    const { isAppMode, getAppMode, appVersion } = initAppmode(unitId);

    const getBaseTrackingData = () => {
        return {
            app_mode: getAppMode(),
            app_version: appVersion,
            user_type: 'employee',
            salesforce_unit_id: unitId,
            address: normalizeAddress(getValues('address')),
        };
    };

    return (
        <styled.App>
            <styled.EstimatorApp ref={scrollUpRef}>
                <VacasaLogo />
                <Header>
                    {isAppMode(APPMODE.ESTIMATE) ? (
                        <Title>Revenue Estimate App</Title>
                    ) : (
                        <Title>Real Estate Projection App</Title>
                    )}
                </Header>
            </styled.EstimatorApp>
            <FormProvider {...methods}>
                <>
                    <styled.Form>
                        <styled.HomeDetails>
                            <styled.Container>
                                <Chip label="ALL FIELDS Required" />

                                <AddressGroup address={address} />
                                <HomeTypeGroup disabled={isFetching} />
                                <RoomsGroup disabled={isFetching} />
                                <AddressVerifySubmitGroup
                                    addressVerified={addressVerified}
                                    disabled={disabledAddressVerifiedBtn || addressVerified}
                                    label="Verify Address"
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        checkAndLogUnitDetails(
                                            'Address verified without full unit details',
                                        );
                                        onVerifyAddressHandler();
                                    }}
                                    variant="outlined"
                                />
                                {showFirstResetBtn && (
                                    <Button
                                        className="reset"
                                        variant="outlined"
                                        onClick={onResetAddress}
                                    >
                                        <Label>Reset</Label>
                                    </Button>
                                )}
                            </styled.Container>
                            {addressVerified && (
                                <styled.Container>
                                    <styled.Description>
                                        Please note that if you leave an optional field blank, our
                                        model will make an educated guess based on comparable Vacasa
                                        units.
                                    </styled.Description>
                                    <SquareFootageGroup disabled={disabledOrBadAddress} />
                                    <MaxOccupancyGroup disabled={disabledOrBadAddress} />
                                    <SurroundingsGroup disabled={disabledOrBadAddress} />
                                    <AmenitiesGroup disabled={disabledOrBadAddress} />
                                    <OwnerHoldsGroup disabled={disabledOrBadAddress} />
                                    <MinRateGroup disabled={disabledOrBadAddress} />
                                    <MinStayGroup disabled={disabledOrBadAddress} />
                                    <SubmitGroup
                                        addressVerified={addressVerified}
                                        disabled={disabled}
                                        label={isRecalculated ? 'Recalculate' : 'Calculate'}
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            setErrorTitle(undefined);
                                            setErrors(undefined);
                                            setEstimateData(undefined); //reseting here because there was a delay in updating the numbers so you would see previous numbers for 1 or 2 seconds before page updated to new values
                                            mutateAsync(undefined, {
                                                onSuccess:
                                                    onGetRentProjectionSuccess(isRecalculated),
                                                onError,
                                            });
                                        }}
                                        variant="outlined"
                                        isLoading={isLoading}
                                    />
                                    {!unitId && (
                                        <Chip
                                            variant="caution"
                                            label="
                                        You are operating in the Standalone
                                        Revenue Estimate App. This estimate is
                                        not tied to a Unit, and if you exit or
                                        refresh this window, your inputs will
                                        not be saved."
                                        />
                                    )}
                                    {errors && (
                                        <ErrorGroup
                                            message={`Unable to generate a projection for ${getValues(
                                                'address',
                                            )}`}
                                            errorName={errorTitle}
                                            detail={errors}
                                        />
                                    )}
                                    <Button
                                        className="reset"
                                        variant="outlined"
                                        onClick={onResetAmenities}
                                    >
                                        <Label>Reset</Label>
                                    </Button>
                                </styled.Container>
                            )}
                        </styled.HomeDetails>
                        {estimateData?.annualPrediction && addressVerified && !isLoading && (
                            <styled.EstimatedRentalRevenue>
                                <styled.Container>
                                    <Header>
                                        <Title>Estimated Rental Revenue</Title>
                                        <Subtitle>
                                            Click Recalculate to update the revenue estimate.
                                        </Subtitle>
                                        <SubSubTitle>
                                            {estimateData?.annual_prediction_source}
                                        </SubSubTitle>
                                    </Header>
                                    <RecalculateGroup
                                        isLoading={isLoading}
                                        estimate={estimateData}
                                    />
                                    <VacasaComps
                                        unitIds={(
                                            estimateData as RentProjectionResponse
                                        )?.unitid_similar?.slice(0, 3)}
                                    />
                                </styled.Container>
                            </styled.EstimatedRentalRevenue>
                        )}
                    </styled.Form>
                </>
            </FormProvider>
        </styled.App>
    );
};
